import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

@Component({
  selector: 'app-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss'],
})
export class PaginationComponent implements OnInit, OnChanges {
  @Input() data: any[] = [];
  @Input() count: number = null;
  @Input() offset: number = null;
  @Input() resultsPerPage: number = 10;
  @Input() totalPages: number = null; // Total de páginas vindo da API
  @Input() currentPageFromApi: number = null; // Página atual vindo da API

  @Input() show: boolean = true;

  @Input() dashPagination: boolean = false;

  @Output() onPageChange = new EventEmitter<number>();
  @Output() onDataChange = new EventEmitter<any[]>();

  currentPage = 1;
  pages = [1];
  lastPage = 1;
  visiblePages = [];
  showLastPage = false;
  showFirstPage = false;

  private isManualPageChange = false;

  constructor() {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.isManualPageChange) {
      this.paginateData();
      return;
    }

    let shouldResetToPageOne = false;

    if (changes.offset && this.offset === 0 && this.currentPage !== 1) {
      shouldResetToPageOne = true;
    }

    if (changes.count && changes.count.previousValue !== undefined) {
      const previousCount = changes.count.previousValue;
      const currentCount = changes.count.currentValue;

      if (
        previousCount !== currentCount &&
        (previousCount === 0 ||
          currentCount === 0 ||
          Math.abs(previousCount - currentCount) > this.resultsPerPage * 2)
      ) {
        shouldResetToPageOne = true;
      }
    }

    if (changes.data && changes.data.previousValue !== undefined) {
      const previousData = changes.data.previousValue;
      const currentData = changes.data.currentValue;

      if (previousData.length === 0 && currentData.length > 0) {
        shouldResetToPageOne = true;
      } else if (previousData.length > 0 && currentData.length === 0) {
        shouldResetToPageOne = true;
      }
    }

    if (shouldResetToPageOne && this.currentPage !== 1) {
      this.currentPage = 1;
    }

    this.paginateData();

    if (changes.count === null) {
      this.count = changes.data.currentValue.length;
    }
  }

  ngOnInit(): void {
    setTimeout(() => {
      this.paginateData();
    }, 1000);

    if (this.count === null) {
      this.count = this.data.length;
    }
  }

  paginateData() {
    // Usar totalPages da API se disponível, senão calcular
    const pageNumber = this.totalPages !== null && this.totalPages !== undefined 
      ? this.totalPages 
      : Math.ceil(this.count / this.resultsPerPage);

    const pages = [];

    for (let i = 1; i <= pageNumber; i++) {
      pages.push(i);
    }

    this.pages = pages;
    this.lastPage = this.pages.length;

    // Usar currentPage da API quando disponível (mais confiável)
    if (this.currentPageFromApi !== null && this.currentPageFromApi !== undefined) {
      // Só atualizar se não foi mudança manual
      if (!this.isManualPageChange) {
        this.currentPage = this.currentPageFromApi;
      }
    } else if (this.offset !== null && this.offset !== undefined && this.resultsPerPage > 0) {
      // Fallback: calcular baseado no offset
      // offset representa o último item mostrado (showing_to)
      // Fórmula: página atual = Math.ceil(offset / resultsPerPage)
      const calculatedPage = Math.ceil(this.offset / this.resultsPerPage);
      
      // Só atualizar se não foi mudança manual
      if (!this.isManualPageChange && calculatedPage > 0) {
        this.currentPage = calculatedPage;
      }
    }

    if (this.lastPage > 0 && this.currentPage > this.lastPage) {
      this.currentPage = 1;
    } else if (this.lastPage === 0) {
      this.currentPage = 1;
    }

    this.setVisiblePages();
  }

  setVisiblePages() {
    this.visiblePages = [];

    if (this.pages.length <= 7) {
      this.visiblePages = this.pages;
    } else {
      if (this.currentPage < 4) {
        // Mostrar primeiras 7 páginas, mas não mais que lastPage
        const maxPages = Math.min(7, this.lastPage);
        for (let i = 1; i <= maxPages; i++) {
          this.visiblePages.push(i);
        }
      } else if (this.currentPage > this.lastPage - 3) {
        // Mostrar últimas páginas
        const startPage = Math.max(1, this.lastPage - 6);
        for (let i = startPage; i <= this.lastPage; i++) {
          this.visiblePages.push(i);
        }
      } else {
        // Mostrar páginas ao redor da atual
        const startPage = Math.max(1, this.currentPage - 3);
        const endPage = Math.min(this.lastPage, this.currentPage + 3);
        for (let i = startPage; i <= endPage; i++) {
          this.visiblePages.push(i);
        }
      }
    }

    // Remover duplicatas e garantir ordem, filtrar páginas inválidas
    this.visiblePages = [...new Set(this.visiblePages)]
      .filter(page => page >= 1 && page <= this.lastPage)
      .sort((a, b) => a - b);

    // Ajustar flags de exibição para evitar duplicação
    // Só mostrar primeira página separada se não estiver em visiblePages
    this.showFirstPage = this.currentPage > 4 && 
                         this.lastPage > 7 && 
                         !this.visiblePages.includes(1);
    
    // Só mostrar última página separada se não estiver em visiblePages
    this.showLastPage = this.currentPage < this.lastPage - 3 && 
                        this.lastPage > 7 && 
                        !this.visiblePages.includes(this.lastPage);
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.isManualPageChange = true;
      this.currentPage--;
      this.setVisiblePages();
      this.onPageChange.emit(this.currentPage);

      setTimeout(() => {
        this.isManualPageChange = false;
      }, 100);
    }
  }

  nextPage() {
    if (this.currentPage < this.pages.length) {
      this.isManualPageChange = true;
      this.currentPage++;
      this.setVisiblePages();
      this.onPageChange.emit(this.currentPage);

      setTimeout(() => {
        this.isManualPageChange = false;
      }, 100);
    }
  }

  changePage(page: number) {
    this.isManualPageChange = true;
    this.currentPage = page;
    this.data = [];

    this.setVisiblePages();

    this.onPageChange.emit(page);

    setTimeout(() => {
      this.isManualPageChange = false;
    }, 100);
  }

  resetToFirstPage() {
    if (this.currentPage !== 1) {
      this.currentPage = 1;
      this.setVisiblePages();
      this.onPageChange.emit(1);
    }
  }

  forceResetToFirstPage() {
    this.currentPage = 1;
    this.setVisiblePages();
  }
}
